import React, { useState, useEffect } from 'react';
import { FiRotateCcw } from 'react-icons/fi';
import { api } from '../api';
import { useMe } from '../App';
import { findPerm } from '../perm';

const ACTIONS = [
  { key: 'can_view', label: 'View' }, { key: 'can_create', label: 'Create' },
  { key: 'can_edit', label: 'Edit' }, { key: 'can_delete', label: 'Delete' },
];

export default function Permissions() {
  const me = useMe();
  const perm = findPerm(me.modules, 'permissions');
  const [mode, setMode] = useState('role'); // 'role' | 'user'
  const [toast, setToast] = useState('');
  function flash(m) { setToast(m); setTimeout(() => setToast(''), 2200); }

  return (
    <div>
      <div className="mod-head">
        <div className="titles">
          <div className="page-eyebrow">Configuration</div>
          <h1>Permissions</h1>
          <div className="count">Set access by role, or fine-tune per individual user</div>
        </div>
      </div>

      <div className="role-tabs" style={{ marginBottom: 18 }}>
        <button className={'role-tab' + (mode === 'role' ? ' active' : '')} onClick={() => setMode('role')}>By Role</button>
        <button className={'role-tab' + (mode === 'user' ? ' active' : '')} onClick={() => setMode('user')}>By User</button>
      </div>

      {mode === 'role'
        ? <RoleGrid perm={perm} flash={flash} />
        : <UserGrid perm={perm} flash={flash} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function Grid({ modules, permOf, onToggle, canEdit }) {
  const cols = [];
  modules.forEach((p) => { cols.push({ ...p, isParent: true }); p.children.forEach((c) => cols.push({ ...c, isParent: false })); });
  return (
    <div className="table-card">
      <table className="data perm-table">
        <thead><tr><th style={{ minWidth: 200 }}>Module</th>
          {ACTIONS.map((a) => <th key={a.key} style={{ textAlign: 'center' }}>{a.label}</th>)}</tr></thead>
        <tbody>
          {cols.map((m) => {
            const p = permOf(m.id);
            return (
              <tr key={m.id}>
                <td style={{ fontWeight: m.isParent ? 600 : 400, paddingLeft: m.isParent ? 18 : 40, color: m.isParent ? 'var(--charcoal)' : '#4A453E' }}>
                  {m.isParent ? m.label : '↳ ' + m.label}
                </td>
                {ACTIONS.map((a) => (
                  <td key={a.key} style={{ textAlign: 'center' }}>
                    <button className={'perm-toggle' + (p[a.key] ? ' on' : '') + (canEdit ? '' : ' locked')}
                      onClick={() => onToggle(m, a.key, p)} disabled={!canEdit}>
                      {p[a.key] ? '✓' : ''}
                    </button>
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function computeNext(current, action) {
  const next = { ...current, [action]: !current[action] };
  if (action === 'can_view' && !next.can_view) { next.can_create = false; next.can_edit = false; next.can_delete = false; }
  if (action !== 'can_view' && next[action]) next.can_view = true;
  return next;
}

function RoleGrid({ perm, flash }) {
  const [data, setData] = useState(null);
  const [role, setRole] = useState(null);
  async function load() {
    try { const d = await api('/permissions'); setData(d); if (d.roles.length && role === null) setRole(d.roles[0].id); }
    catch (e) { flash(e.message); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  if (!data) return <div className="empty">Loading…</div>;

  const permOf = (mid) => data.permissions[role + '|' + mid] || { can_view: false, can_create: false, can_edit: false, can_delete: false };
  async function toggle(m, action, current) {
    const next = computeNext(current, action);
    const body = { role_id: role, module_id: m.id, ...next };
    try { await api('/permissions', { method: 'PUT', body });
      setData((d) => ({ ...d, permissions: { ...d.permissions, [role + '|' + m.id]: body } })); }
    catch (e) { flash(e.message); }
  }

  return (
    <>
      <div className="role-tabs">
        {data.roles.map((r) => <button key={r.id} className={'role-tab' + (role === r.id ? ' active' : '')} onClick={() => setRole(r.id)}>{r.name}</button>)}
      </div>
      <Grid modules={data.modules} permOf={permOf} onToggle={toggle} canEdit={perm.can_edit} />
      <p className="page-desc" style={{ marginTop: 14, fontSize: 13 }}>Applies to <strong>all users</strong> of this role who haven’t been individually customised.</p>
    </>
  );
}

function UserGrid({ perm, flash }) {
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => { api('/permissions/users').then(setUsers).catch((e) => flash(e.message)); /* eslint-disable-next-line */ }, []);
  async function loadUser(id) {
    setUserId(id); setData(null);
    if (!id) return;
    try { setData(await api('/permissions/user/' + id)); } catch (e) { flash(e.message); }
  }

  const permOf = (mid) => (data && data.permissions[mid]) || { can_view: false, can_create: false, can_edit: false, can_delete: false };
  async function toggle(m, action, current) {
    const next = computeNext(current, action);
    try {
      await api('/permissions/user/' + userId, { method: 'PUT', body: { module_id: m.id, ...next } });
      setData((d) => ({ ...d, customised: true, permissions: { ...d.permissions, [m.id]: { module_id: m.id, ...next } } }));
      // refresh customised flag in the picker
      setUsers((us) => us.map((u) => u.id === Number(userId) ? { ...u, customised: true } : u));
    } catch (e) { flash(e.message); }
  }
  async function reset() {
    if (!window.confirm('Reset this user back to their role’s permissions? Their custom settings will be removed.')) return;
    try { await api('/permissions/user/' + userId, { method: 'DELETE' }); flash('Reset to role'); loadUser(userId);
      setUsers((us) => us.map((u) => u.id === Number(userId) ? { ...u, customised: false } : u)); }
    catch (e) { flash(e.message); }
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <select className="status-select" style={{ minWidth: 240 }} value={userId} onChange={(e) => loadUser(e.target.value)}>
          <option value="">Select a user…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.role_name}{u.customised ? ' (custom)' : ''}</option>)}
        </select>
        {data && data.customised && perm.can_edit && (
          <button className="btn btn-sm" onClick={reset}><FiRotateCcw /> Reset to role</button>
        )}
      </div>

      {!userId ? <div className="empty">Pick a user to view and set their individual access.</div>
        : !data ? <div className="empty">Loading…</div>
        : (
          <>
            <div className="page-desc" style={{ marginBottom: 12, fontSize: 13 }}>
              {data.customised
                ? <span><strong>{data.user.name}</strong> has custom access (overrides their {data.user.role_name} role).</span>
                : <span><strong>{data.user.name}</strong> currently follows the <strong>{data.user.role_name}</strong> role. Toggle any box to give them custom access.</span>}
            </div>
            <Grid modules={data.modules} permOf={permOf} onToggle={toggle} canEdit={perm.can_edit} />
          </>
        )}
    </>
  );
}
